import { useMemo, useState } from 'react';
import { NoteCard } from '../components/note/NoteCard';
import { Segmented, Sheet } from '../components/ui/ui';
import { uid } from '../lib/id';
import type { Design, Note, Pattern } from '../storage/schemas';
import { allFonts, fontStack } from './fonts';
import { designLook } from './look';

const SWATCHES = ['#000000', '#FFFFFF', '#F1D100', '#968100', '#E15651', '#FF6120', '#F5879E', '#7752DD', '#475ADC', '#367ED4', '#43BDAE', '#41D34A', '#2B6153', '#5D4C44', '#EDE7D6', '#B3B3B3'];

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const id = useMemo(() => uid('c'), []);
  return (
    <div className="de-color">
      <label htmlFor={id}>{label}</label>
      <div className="de-swatches">
        {SWATCHES.map((c) => (
          <button key={c} type="button" className={`de-swatch ${value.toUpperCase() === c ? 'is-on' : ''}`} style={{ background: c }} aria-label={`${label} ${c}`} aria-pressed={value.toUpperCase() === c} onClick={() => onChange(c)} />
        ))}
        <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={`Custom ${label.toLowerCase()} colour`} />
      </div>
    </div>
  );
}

/** Create or edit a custom design ("My Designs"). */
export function DesignEditorSheet({ design, note, onClose, onSave }: { design: Design; note: Note; onClose: () => void; onSave: (d: Design) => void }) {
  const [d, setD] = useState<Design>(design);
  const set = (patch: Partial<Design>) => setD((x) => ({ ...x, ...patch }));
  const preview = { ...note, images: [], strokes: [] };
  return (
    <Sheet open onClose={onClose} title={design.source === 'custom' && design.createdAt !== design.updatedAt ? `Edit ${design.name}` : 'New design'} tall>
      <div className="de-pane">
        <div className="de-preview">
          <NoteCard note={preview} look={designLook(d)} />
        </div>
        <label className="de-field">
          <span>Name</span>
          <input className="text-input" value={d.name} maxLength={60} onChange={(e) => set({ name: e.target.value || design.name })} />
        </label>
        <ColorField label="Paper" value={d.paper} onChange={(v) => set({ paper: v })} />
        <ColorField label="Text" value={d.ink} onChange={(v) => set({ ink: v, accent: v })} />
        <ColorField label="Lines" value={d.line} onChange={(v) => set({ line: v })} />
        <div className="de-field">
          <span>Paper style</span>
          <Segmented<Pattern>
            label="Paper style"
            value={d.pattern}
            onChange={(v) => set({ pattern: v })}
            options={[
              { value: 'grid', label: 'Grid' },
              { value: 'ruled', label: 'Lined' },
              { value: 'dots', label: 'Dots' },
              { value: 'plain', label: 'Plain' },
            ]}
          />
        </div>
        <label className="de-field">
          <span>Pattern size</span>
          <input type="range" min={0.5} max={2} step={0.25} value={d.patternScale} onChange={(e) => set({ patternScale: Number(e.target.value) })} />
        </label>
        <label className="de-field">
          <span>Opacity {Math.round((d.paperAlpha ?? 1) * 100)}%</span>
          <input type="range" min={0.1} max={1} step={0.05} value={d.paperAlpha ?? 1} onChange={(e) => set({ paperAlpha: Number(e.target.value) >= 1 ? undefined : Number(e.target.value) })} />
        </label>
        <label className="de-field">
          <span>Font</span>
          <select className="text-input" value={d.font} onChange={(e) => set({ font: e.target.value })} style={{ fontFamily: fontStack(d.font) }}>
            {allFonts().map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <div className="de-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn is-primary" onClick={() => onSave({ ...d, category: 'Custom', source: 'custom', border: undefined, decoration: d.decoration })}>
            Save design
          </button>
        </div>
      </div>
    </Sheet>
  );
}
